import { IS_DEV } from "@/config/constants";
import SoundService from "@/game/SoundService";
import useGameTicker from "@/hooks/useGameTicker";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useUserStore from "@/store/useUserStore";
import { SkinTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { FlutterAdapter } from "./adapters/FlutterAdapter/FlutterAdapter";
import { SteamAdapter } from "./adapters/SteamAdapter";

// TODO: markRaw vo Vue komponentoch

export const enum AnalyticsEvents {
	AD_VIEWED = "sng_ad_view",
	PURCHASE = "af_purchase", // Toto sa posiela s: af_revenue; af_currency; af_quantity
	LEVEL_2_ACHIEVED = "level_2_achieved",
	LEVEL_5_ACHIEVED = "level_5_achieved",
}

export enum LinkTypes {
	WITH_OVERRIDE = "withOverride",
	WITH_EXIST = "withExist",
}

export enum SocialMediaType {
	GOOGLE = "google",
	APPLE = "apple",
	NONE = "",
}

export interface Adapter {
	firstTimeClicked(): void;
	loadingDone(): void;
	setLoadingProgress(progress: number): void;
	playRewardedAd(): void;
	sendAnalyticsEvent(
		event: AnalyticsEvents,
		data?: Record<string, number | string>,
	): void;
	buyProduct(productId: string | number): void;
	setLanguage(language: string): void;
	openStore(): void;
	exitApp(): void;
	downloadImage(base64: string): void;
	linkAccount(type?: LinkTypes, socialMediaType?: SocialMediaType): void;
	resetGame(): void;
	relogUser(): void;
	sendGameVersion(version: string): void;
	openSecondWebview(url: string): void;
	closeSecondWebview(): void;
	showPrivacyOptionsFormUmp(): void;
	canVibrate?(): void;
	vibrate?(vibrationTimeMs: number): void;
	showErrorDuringLoading(errorText: string): void;
}

class AppCommunicator {
	private readonly adapter: Adapter;
	private vibrationAllowed = false;
	private rewardedAdAvailable = false;
	private loadingProgress = 0;

	private static instance: AppCommunicator;

	private constructor() {
		const isFlutterApp = true; // TODO:
		this.adapter = isFlutterApp ? new FlutterAdapter() : new SteamAdapter();
	}

	public static getInstance(): AppCommunicator {
		if (!this.instance) this.instance = new this();
		return this.instance;
	}

	public get getLoadingProgress(): number {
		return this.loadingProgress;
	}

	public setVibrate(value: boolean): void {
		this.vibrationAllowed = value;
	}

	public firstTimeClicked(): void {
		this.adapter.firstTimeClicked();
	}

	public loadingDone(): void {
		this.adapter.loadingDone();
	}

	public setLoadingProgress(progress: number): void {
		this.loadingProgress = progress;
		this.adapter.setLoadingProgress(progress);
	}

	public playRewardedAd(): void {
		if (this.isRewardedAdAvailable) {
			useUserStore().setIsAdPlaying(true);
			useGameTicker().stop();
			SoundService.getInstance().appWentBackground();
			this.adapter.playRewardedAd();
		}
	}

	public sendAnalyticsEvent(
		event: AnalyticsEvents,
		data?: Record<string, number | string>,
	): void {
		this.adapter.sendAnalyticsEvent(event, data);
	}

	public buyProduct(productId: string | number): void {
		this.adapter.buyProduct(productId);
	}

	public setLanguage(language: string): void {
		this.adapter.setLanguage(language);
	}

	public openStore(): void {
		useUserStore().setAppRated(true);
		useSocketService()
			.emitLoadRequest({
				stores: [EmitStores.USER],
			})
			.then((): void => {
				this.adapter.openStore();
			});
	}

	/**
	 * Po zavolani da appku do backgroundu.
	 */
	public exitApp(): void {
		this.adapter.exitApp();
	}

	public downloadImage(base64: string): void {
		this.adapter.downloadImage(base64);
	}

	public linkAccount(
		type?: LinkTypes,
		socialMediaType?: SocialMediaType,
	): void {
		this.adapter.linkAccount(type, socialMediaType);
		const popupStore = usePopupStore();
		popupStore.showPopup({
			id: PopupDictionary.ACCOUNT_LINKING,
			type: PopupType.INFO,
			heading: translate("ui.account_linking"),
			body: translate("ui.loading.non_translatable"),
			skin: SkinTypes.LIGHT,
			isCentered: true,
		});
	}

	public resetGame(): void {
		this.adapter.resetGame();
	}

	public relogUser(): void {
		if (IS_DEV) useSocketService().reload(true);
		else this.adapter.relogUser();
	}

	public sendGameVersion(version: string): void {
		this.adapter.sendGameVersion(version);
	}

	public openSecondWebview(url: string): void {
		this.adapter.openSecondWebview(url);
	}

	public closeSecondWebview(): void {
		this.adapter.closeSecondWebview();
	}

	/**
	 * Po zavolani vyvola zresetovane CMP okno.
	 * Tento event resp. funkcia mat v hre moznost znovu vyvolat CMP okno
	 * je povinna pre niektore krajiny.
	 * To ci je dany button potrebny Flutteru povie AdMob a Flutter potom,
	 * ak je dany button potrebny, zavola tuto metodu -> showPrivacyOptionsSettings()
	 */
	public showPrivacyOptionsFormUmp(): void {
		this.adapter.showPrivacyOptionsFormUmp();
	}

	/**
	 * Opyta sa Fluttera, ci moze vibrovat.
	 * Flutter komunikuje odpoved zavolanim sendVibrationInfo
	 */
	public canVibrate(): void {
		if (this.adapter.canVibrate) {
			this.adapter.canVibrate();
		} else {
			// eslint-disable-next-line no-console
			console.warn("Vibration check is not supported on this platform.");
		}
	}

	public vibrate(vibrationTimeMs: number): void {
		if (!this.vibrationAllowed) {
			// eslint-disable-next-line no-console
			console.warn("Vibration is not allowed.");
			return;
		}
		if (this.adapter.vibrate) {
			this.adapter.vibrate(vibrationTimeMs);
			return;
		} else {
			// eslint-disable-next-line no-console
			console.warn("Vibration is not supported on this platform.");
		}
	}

	public rewardedAdLoaded(): void {
		this.rewardedAdAvailable = true;
	}

	public get isRewardedAdAvailable(): boolean {
		return this.rewardedAdAvailable;
	}

	public showErrorDuringLoading(errorText = "Unknown error"): void {
		this.adapter.showErrorDuringLoading(errorText);
	}
}

export default AppCommunicator;
